/**
 * A service token in the record panel: its scope and its deadline. Rotation
 * and revocation stay on the Security rows, since rotation reveals a secret
 * once and that dialog is theirs.
 */
import { useQuery } from '@tanstack/react-query';

import { listServiceTokens } from '@optum/api-client/endpoints/service-tokens';

import type { RecordRef } from '../../components/shell/record.js';
import {
  RecordColumn,
  RecordFacts,
  RecordIdentity,
  RecordListError,
  RecordMissing,
  RecordSection,
} from '../../components/shell/RecordParts.js';
import { LoadingBlock } from '../../ui/AsyncState.js';
import { absoluteDate, shortId } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { expiryStatus } from '../../lib/token-expiry.js';
import { Badge, IconTile, Typography } from '../../ui/finixt.js';

export function ServiceTokenRecord({ record }: { record: RecordRef }) {
  const tokens = useQuery({ queryKey: qk.serviceTokens(), queryFn: listServiceTokens });
  if (tokens.isPending) return <LoadingBlock />;
  if (tokens.isError) {
    return (
      <RecordListError
        message={tokens.error instanceof Error ? tokens.error.message : 'Failed to load'}
      />
    );
  }
  const t = tokens.data.find((row) => row.id === record.id);
  if (!t) return <RecordMissing record={record} />;
  const expiry = expiryStatus(t.expires_at);
  const tone = expiry.state === 'expired' ? 'danger' : expiry.state === 'due' ? 'warn' : 'neutral';

  return (
    <RecordColumn>
      <RecordIdentity
        avatar={<IconTile icon="key" size={40} tint="var(--warn-tint)" color="var(--warn)" />}
        eyebrow={
          <>
            <Typography variant="eyebrow">Service token</Typography>
            <Badge tone={tone} mono shape="square">
              {t.expires_at ? expiry.state : 'undated'}
            </Badge>
          </>
        }
        name={t.label}
        subtitle={t.scope}
      />
      <RecordSection label="Facts">
        <RecordFacts
          items={[
            { label: 'ID', value: shortId(t.id) },
            { label: 'Scope', value: t.scope },
            { label: 'Issued', value: absoluteDate(t.created_at) },
            { label: 'Rotated', value: t.rotated_at ? absoluteDate(t.rotated_at) : 'never' },
            { label: 'Expires', value: t.expires_at ? absoluteDate(t.expires_at) : 'undated' },
            {
              label: 'Previous retires',
              value: t.previous_retires_at ? absoluteDate(t.previous_retires_at) : '—',
            },
          ]}
        />
      </RecordSection>
    </RecordColumn>
  );
}
